import React, { useCallback, useState, useMemo } from 'react';
import { Button } from 'reactstrap';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import {
  individual_column_style,
  individual_header_style,
  icon_column_style,
  individual_icon_header_style
} from '../../../../../../../../assets/styles/table_custom_style';
import { useHistory } from 'react-router-dom';
import { toBrazilianFullDate } from 'views/CRM/shared/utils/date';
import AccountService from '../../../../../services/accountService';
import { toast } from 'react-toastify';
import ModalConfirmation from 'components/Modal/ConfirmationModal';
import './styles.css';
import { maskCpf } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { maskCnpj } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { formatCurrency } from 'utils/fuctions/formatCurrency';
import FlexTable from 'components/FlexTable/index.jsx';
import { PhoneCell } from 'components/FlexTable/components/cells/phone';
import { verifyRoles } from 'components/Authentication/VerifyRoles';

/**
 * @typedef {Object} Column
 * @property {string} dataField
 * @property {string} text
 * @property {Object} [style]
 * @property {Object} [headerStyle]
 * @property {boolean} [sort]
 * @property {Function} [formatter]
 * @property {string} [name]
 * @property {string} [type]
 * @property {string} [label]
 */

/**
 * @typedef {Object} SortConfig
 * @property {string} key
 * @property {string} direction
 */

/**
 * @typedef {Object} Account
 * @property {string} id
 * @property {string} name
 * @property {string} telephone
 * @property {string} createdAt
 * @property {string} createdByName
 * @property {string} lastUpdatedAt
 * @property {string} recordTypeId
 */

/**
 * @param {Object} props
 * @param {Account[]} props.accounts
 * @param {Function} props.reloadAccounts
 * @param {Column[]} props.customFields
 * @param {Function} props.setSortConfig
 * @param {SortConfig} props.sortConfig
 */
const ListAccountsTable = ({
  accounts,
  reloadAccounts,
  customFields,
  setSortConfig,
  sortConfig,
  selectedRows,
  handleOnSelect,
  handleOnSelectAll,
  batchEdit
}) => {
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const history = useHistory();

  /**
   * Handle account deletion.
   * @param {string} id
   */
  const handleDeleteAccount = useCallback(
    (id) => {
      AccountService.deleteAccount(id)
        .catch((err) => {
          const message =
            err?.response?.data?.message || 'Contato deletado com sucesso';
          toast.error(message);
        })
        .finally(() => {
          setOpenModalConfirmation(false);
          reloadAccounts();
        });
    },
    [reloadAccounts]
  );

  /**
   * Navigate to the edit account page.
   * @param {Account} account
   */
  const navigateToEditAccountPage = (account) => {
    history.push(`/admin/crm/accounts/${account.recordTypeId}/${account.id}`);
  };

  /**
   * Control the deletion of an account.
   * @param {string} id
   */
  const controlDeleteAccount = (id) => {
    setAccountId(id);
    setOpenModalConfirmation(true);
  };

  /**
   * Create dynamic columns for the table.
   * @returns {Column[]}
   */
  const createDynamicColumns = useCallback(() => {
    const columns = [];

    columns.push(
      {
        dataField: 'name',
        text: 'Nome',
        sort: true,
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_, row) => {
          return (
            <>
              {!row.isConverted && verifyRoles(['CRM_ACCOUNTS_EDIT']) ? (
                <span
                  className="account-name"
                  onClick={() => {
                    navigateToEditAccountPage(row);
                  }}
                >
                  {row.name}
                </span>
              ) : (
                <span>{row.name}</span>
              )}
            </>
          );
        }
      },
      {
        dataField: 'telephone',
        text: 'Telefone',
        style: individual_column_style,
        formatter: (_, row) => {
          if (!row?.phone) return null;
          return <PhoneCell phone={row?.phone} ddi={row?.ddi} />;
        }
      },
      {
        dataField: 'responsible',
        text: 'Responsável',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, cell) => {
          return cell.responsibleName;
        }
      },
      {
        dataField: 'createdAt',
        text: 'Criado em',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (row) => {
          if (row) return toBrazilianFullDate(row);
        }
      },
      {
        dataField: 'createdBy',
        text: 'Criado por',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, cell) => {
          return cell.createdByName;
        }
      },
      {
        dataField: 'lastUpdatedAt',
        text: 'Última atualização',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (row) => {
          if (row) return toBrazilianFullDate(row);
        }
      }
    );

    (customFields || []).forEach((field) => {
      columns.push({
        dataField: field.name,
        text: field.label,
        style: individual_column_style,
        formatter: (_, row) => {
          const value = row[field.name];

          if (value && typeof value === 'object') {
            const vddi = value.ddi;
            const vnumber = value.number;
            if (vddi && vnumber) {
              return <PhoneCell phone={vnumber} ddi={vddi} />;
            }
            return null;
          }

          if (field.type === 'DATE' && !!value) {
            return new Date(value).toLocaleDateString('pt-br');
          } else if (field.type === 'TELEPHONE' && value) {
            const vddi = row.ddi;
            const vnumber = value;
            if (!vddi || !vnumber) return null;
            return <PhoneCell phone={vnumber} ddi={vddi} />;
          } else if (field.type === 'CPF' && value) {
            return maskCpf(value);
          } else if (field.type === 'CNPJ' && value) {
            return maskCnpj(value);
          } else if (field.type === 'CURRENCY' && value) {
            return formatCurrency(value);
          }

          return value;
        }
      });
    });

    columns.push({
      dataField: 'edit',
      text: 'Editar',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <>
            {verifyRoles(['CRM_ACCOUNTS_EDIT']) ? (
              <Button
                size="sm"
                color="info"
                onClick={() => navigateToEditAccountPage(row)}
              >
                <li className="fas fa-user-edit"></li>
              </Button>
            ) : null}
          </>
        );
      }
    });

    columns.push({
      dataField: 'delete',
      text: 'Excluir',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <>
            {!row.isConverted && verifyRoles(['CRM_ACCOUNTS_DELETE']) ? (
              <Button
                size="sm"
                color="danger"
                onClick={() => controlDeleteAccount(row.id)}
              >
                <li className="fas fa-trash"></li>
              </Button>
            ) : null}
          </>
        );
      }
    });

    return columns;
  }, [customFields, navigateToEditAccountPage]);

  const dynamicFunctionColumns = useMemo(
    () => createDynamicColumns(),
    [createDynamicColumns]
  );

  return (
    <>
      <ToolkitProvider
        data={accounts || []}
        keyField="id"
        columns={dynamicFunctionColumns}
      >
        {(props) => (
          <div className="py-4 table-responsive">
            <FlexTable
              {...props.baseProps}
              entityName="account"
              setSortConfig={setSortConfig}
              sortConfig={sortConfig}
              selectRow={batchEdit}
              onSelect={handleOnSelect}
              onSelectAll={handleOnSelectAll}
              selectedRows={selectedRows}
            />
          </div>
        )}
      </ToolkitProvider>

      <ModalConfirmation
        isModalOpen={openModalConfirmation}
        onModalToggle={setOpenModalConfirmation}
        unmountOnClose={true}
        idResource={accountId}
        deleteResource={handleDeleteAccount}
        message={
          'Tem certeza que deseja excluir esta conta? Todas as oportunidades e contatos relacionados a ela serão juntamente excluídas.'
        }
      />
    </>
  );
};

export default ListAccountsTable;
